import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getLocale,
  setLocale as persistLocale,
  subscribeLocale,
  translate,
  type Locale,
  type TranslateParams,
} from '../lib/i18n';

// ---------------------------------------------------------------------------
// I18n — React context (CLE-277).
//
// Thin React wrapper over the module-level locale state in lib/i18n.ts.
// Components read `locale`, call `setLocale()` to switch language, and use
// `t(key)` to translate UI strings. Changing the locale updates state for all
// consumers instantly (no page reload) and persists the choice to localStorage.
// ---------------------------------------------------------------------------

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: TranslateParams) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getLocale);

  // Keep React state in sync if the module-level locale changes elsewhere.
  useEffect(() => subscribeLocale(setLocaleState), []);

  // Reflect the initial locale on <html lang> for accessibility / SEO.
  useEffect(() => {
    try {
      document.documentElement.lang = locale;
    } catch {
      // ignore (non-browser env)
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => persistLocale(next), []);

  // `t` depends on `locale` so consumers re-render with the right strings when
  // the language flips.
  const t = useCallback(
    (key: string, params?: TranslateParams) => translate(key, locale, params),
    [locale],
  );

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return ctx;
}
